/**
 * Starter-block install offer (SA-31).
 *
 * Opened by WorkflowPickerModal right after a workflow whose template
 * manifest carries `persona_blocks` is installed into the draft bundle.
 * Lists each recommended block with its taxonomy label and a preview;
 * blocks whose category the draft already holds default to unchecked
 * (keep-mine, the SA-84 R3 convention) with an inline hint. Add appends
 * the checked blocks to the DRAFT via composerStore.installStarterBlocks
 * — nothing is saved until the operator saves the Workshop draft; Skip
 * keeps the freshly-installed binding and adds no blocks.
 *
 * Mount-per-offer: the parent mounts this component when an offer
 * exists (keyed by workflow), so classification and default checks are
 * computed once, at mount, against the draft as it stood then.
 */
import { useState } from 'react'
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalDescription,
  ModalBody,
  ModalFooter,
} from '@/components/ui/Modal'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Switch } from '@/components/ui/Switch'
import { useComposerStore } from '@/stores/composerStore'
import { classifyStarterBlocks } from '@/utils/starterBlocks'
import { BLOCK_TAXONOMY } from './blockTaxonomy'
import type { PersonaStarterBlock } from '@/hooks/useWorkflows'

export interface StarterBlocksOfferDialogProps {
  workflowName: string
  blocks: PersonaStarterBlock[]
  onClose: () => void
}

function categoryLabel(category: string): string {
  const meta = BLOCK_TAXONOMY[category as keyof typeof BLOCK_TAXONOMY]
  return meta ? meta.label : category
}

function categoryBadge(category: string): string {
  const meta = BLOCK_TAXONOMY[category as keyof typeof BLOCK_TAXONOMY]
  return meta ? `${meta.emoji} ${meta.label}` : category
}

export function StarterBlocksOfferDialog({
  workflowName,
  blocks,
  onClose,
}: StarterBlocksOfferDialogProps) {
  const installStarterBlocks = useComposerStore((s) => s.installStarterBlocks)
  const [classified] = useState(() => {
    const { draftBundle, serverBundle } = useComposerStore.getState()
    return classifyStarterBlocks(
      (draftBundle ?? serverBundle)?.blocks ?? [],
      blocks,
    )
  })
  const [checked, setChecked] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(
      classified.map((e) => [e.block.ref, !e.hasCategoryConflict]),
    ),
  )

  const selected = classified.filter((e) => checked[e.block.ref])

  const handleAdd = () => {
    installStarterBlocks(
      selected.map(({ block }) => ({
        category: block.category,
        content: block.content,
      })),
    )
    onClose()
  }

  return (
    <Modal
      open
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose()
      }}
    >
      <ModalContent>
        <ModalHeader>
          <ModalTitle>Recommended starter blocks</ModalTitle>
          <ModalDescription>
            {workflowName} recommends these persona blocks — vetted
            conversational lessons this workflow works best with. They are a
            starting point: edit or remove them like any other block. Nothing
            is saved until you save the draft.
          </ModalDescription>
        </ModalHeader>
        <ModalBody>
          <ul className="space-y-3">
            {classified.map(({ block, hasCategoryConflict }) => (
              <li key={block.ref} className="rounded-sm border border-border p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline">{categoryBadge(block.category)}</Badge>
                      <span className="text-sm font-medium">{block.title}</span>
                    </div>
                    {hasCategoryConflict && (
                      <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                        This persona already has a {categoryLabel(block.category)}{' '}
                        block — left off by default; review for overlap before
                        adding.
                      </p>
                    )}
                    <details className="mt-2">
                      <summary className="text-xs text-muted-foreground cursor-pointer select-none">
                        Preview
                      </summary>
                      <p className="mt-1 text-xs text-muted-foreground whitespace-pre-wrap">
                        {block.content}
                      </p>
                    </details>
                  </div>
                  <Switch
                    size="sm"
                    checked={!!checked[block.ref]}
                    onCheckedChange={(value) =>
                      setChecked((prev) => ({ ...prev, [block.ref]: value }))
                    }
                    aria-label={`include ${block.title}`}
                  />
                </div>
              </li>
            ))}
          </ul>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={onClose}>
            Skip
          </Button>
          <Button onClick={handleAdd} disabled={selected.length === 0}>
            Add {selected.length} block{selected.length === 1 ? '' : 's'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
